import * as React from 'react';

import * as styles from './Gallery.css';
import { GalleryFullscreen } from './components/GalleryFullscreen';
import { GalleryInner } from './components/GalleryInner';
import { InfoControls } from './components/InfoControls';
import { TMedia } from './types';

interface IGallerySlots {
  slotFullscreenBefore?: React.ComponentProps<typeof GalleryFullscreen>['slotBefore'];
  slotFullscreenSidebar?: React.ComponentProps<typeof GalleryFullscreen>['slotSidebar'];
  slotScrollerAfter?: React.ReactElement;
}

interface IGalleryProps extends IGallerySlots {
  media: TMedia[];
  onFullscreenOpen?(): void;
}

const GalleryComponent: React.FC<IGalleryProps> = ({
  slotScrollerAfter,
  slotFullscreenBefore,
  slotFullscreenSidebar,
  media,
  onFullscreenOpen,
}) => {
  const galleryInnerRef = React.useRef<React.ElementRef<typeof GalleryInner>>(null);
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [isFullscreenOpen, setIsFullscreenOpen] = React.useState(false);

  /* istanbul ignore next */
  const handleSlideChange = React.useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  /* istanbul ignore next */
  const handleFullscreenSlideChange = React.useCallback((index: number) => {
    galleryInnerRef.current?.scrollTo(index, { behavior: 'auto' });
    setCurrentSlide(index);
  }, []);

  const handleScrollerClick = React.useCallback(() => {
    setIsFullscreenOpen(true);

    /* istanbul ignore else */
    if (onFullscreenOpen) {
      onFullscreenOpen();
    }
  }, [onFullscreenOpen]);

  const handleFullscreenClose = React.useCallback(() => {
    setIsFullscreenOpen(false);
  }, []);

  if (media.length === 0) {
    return <div data-testid="noMedia" className={styles['no-media']} />;
  }

  return (
    <div data-testid="GalleryContainer" className={styles['container']}>
      <GalleryInner
        ref={galleryInnerRef}
        bordered
        fullscreenable
        keyboard={!isFullscreenOpen}
        media={media}
        objectFit="contain"
        slotScrollerEnd={
          <>
            {slotScrollerAfter}
            <InfoControls media={media} />
          </>
        }
        onSlideChange={handleSlideChange}
        onScrollerClick={handleScrollerClick}
        onFullscreenClick={handleScrollerClick}
      />
      <GalleryFullscreen
        currentSlide={currentSlide}
        slotBefore={slotFullscreenBefore}
        slotSidebar={slotFullscreenSidebar}
        media={media}
        initialSlide={currentSlide}
        open={isFullscreenOpen}
        onSlideChange={handleFullscreenSlideChange}
        onClose={handleFullscreenClose}
      />
    </div>
  );
};

export const Gallery = React.memo(GalleryComponent);
